import React, { useCallback, useRef } from "react";
import { Observer, useLocalStore } from "mobx-react";
import {
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@material-ui/core";
import { Add, Delete, FileCopy } from "@material-ui/icons";
import locale from "@bonliva-crm/locale";
import { ContractsPriceTemplateSearchStore } from "./ContractsPriceTemplateSearchStore";
import { store } from "@store";

type Props = {
  onItemOpen: (id: string) => void;
  onNewItem: () => void;
};

export const ContractsPriceTemplatesSearch = ({ onItemOpen, onNewItem }: Props) => {
  const localStore = useLocalStore(() => new ContractsPriceTemplateSearchStore());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        localStore.setTextFilter(value);
      }, 300);
    },
    [localStore]
  );

  const handleDelete = useCallback(
    (id: string) => {
      store().ui.confirmDelete(`${locale.Deleted}`, (confirmed) => {
        if (!confirmed) return;
        localStore.delete(id);
      });
    },
    [localStore]
  );

  return (
    <Observer>
      {() => (
        <div style={{ padding: 16, flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%" }}>
          <Toolbar disableGutters>
            <Typography variant="h6" style={{ flex: 1 }}>
              Kontraktsmallar
            </Typography>
            <TextField
              label={locale.Search}
              defaultValue={localStore.textFilter}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              style={{ marginRight: 16 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={onNewItem}
            >
              {locale.New}
            </Button>
          </Toolbar>

          <Paper style={{ marginTop: 16 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{locale.Name}</TableCell>
                  <TableCell>{locale.Type}</TableCell>
                  <TableCell>Synlighet</TableCell>
                  <TableCell>Prisrader</TableCell>
                  <TableCell>Skapad av</TableCell>
                  <TableCell>Skapad</TableCell>
                  <TableCell align="right">{locale.Actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localStore.items.map((item) => (
                  <TableRow
                    key={item._id}
                    hover
                    onClick={() => onItemOpen(item._id)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell style={{ fontWeight: 500 }}>
                      {item.name}
                    </TableCell>
                    <TableCell>
                      {item.type
                        ? locale.contractsPrice.priceTemplateType[item.type]
                        : "–"}
                    </TableCell>
                    <TableCell>
                      {item.private
                        ? <Chip size="small" label="Privat" />
                        : <Chip size="small" label="Publik" color="primary" />}
                    </TableCell>
                    <TableCell>{item.records?.length ?? 0}</TableCell>
                    <TableCell>{item.createdByName ?? "–"}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          item.updatedByName
                            ? `Uppdaterad av ${item.updatedByName}, ${new Date(item.updatedDate).toLocaleDateString("sv-SE")}`
                            : ""
                        }
                        placement="top"
                      >
                        <span>
                          {item.createdDate
                            ? new Date(item.createdDate).toLocaleDateString("sv-SE")
                            : "–"}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" title={locale.Duplicate} onClick={() => localStore.duplicate(item)}>
                        <FileCopy fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title={locale.Delete} onClick={() => handleDelete(item._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {localStore.items.length === 0 && !localStore.loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Inga kontraktsmallar hittades
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={localStore.total}
              page={localStore.page}
              onPageChange={(_, newPage) => localStore.setPage(newPage)}
              rowsPerPage={localStore.rowsPerPage}
              onRowsPerPageChange={(e) => localStore.setRowsPerPage(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Rader per sida:"
            />
          </Paper>
        </div>
      )}
    </Observer>
  );
};
